import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Modal } from '../common';
import { X, CheckCircle, XCircle, ArrowLeft, Loader2 } from 'lucide-react';

const StudentAssignModal = ({ isOpen, onClose, presetId, presetName, onAssignSuccess }) => {
  const { getAccessToken, userId, userType } = useAuth();
  const navigate = useNavigate();

  // Step 관리: 1=검색/선택, 2=확인, 3=결과
  const [step, setStep] = useState(1);

  // Step 1: 학생 목록
  const [searchQuery, setSearchQuery] = useState('');
  const [lectureQuery, setLectureQuery] = useState('');
  const [teacherQuery, setTeacherQuery] = useState('');
  const [allStudents, setAllStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);
  const tableContainerRef = useRef(null);
  const [tableHeight, setTableHeight] = useState(null);

  // Step 3: 결과
  const [results, setResults] = useState([]);
  const [isAssigning, setIsAssigning] = useState(false);

  // 모달 닫기 시 상태 초기화
  const handleClose = useCallback(() => {
    setStep(1);
    setSearchQuery('');
    setLectureQuery('');
    setTeacherQuery('');
    setAllStudents([]);
    setSelectedStudents([]);
    setResults([]);
    setIsAssigning(false);
    setTableHeight(null);
    onClose();
  }, [onClose]);

  // 모달 열릴 때 전체 학생 로딩
  useEffect(() => {
    if (!isOpen) return;

    const fetchAllStudents = async () => {
      setIsLoadingStudents(true);
      try {
        const token = await getAccessToken();
        if (!token) {
          navigate('/signin');
          return;
        }

        const buildUrl = (page) => {
          const params = new URLSearchParams({ page, per_page: 1000 });
          if (userType === 'manager') {
            return `${process.env.REACT_APP_API_BASE_URL}/api/users/student?${params}`;
          }
          params.append('teacher_id', userId);
          return `${process.env.REACT_APP_API_BASE_URL}/api/users/student/allocate?${params}`;
        };

        // 첫 페이지 조회 → total_pages 확인
        const firstResponse = await fetch(buildUrl(1), {
          headers: { 'Authorization': token, 'Content-Type': 'application/json' }
        });

        if (firstResponse.status === 401) {
          navigate('/signin');
          return;
        }
        if (!firstResponse.ok) throw new Error(`HTTP error! status: ${firstResponse.status}`);

        const firstData = await firstResponse.json();
        if (firstData.status !== 'success') {
          setAllStudents([]);
          setIsLoadingStudents(false);
          return;
        }

        let collected = [...(firstData.data || [])];
        const totalPages = firstData.total_pages || 1;

        // 나머지 페이지 순회
        for (let page = 2; page <= totalPages; page++) {
          const res = await fetch(buildUrl(page), {
            headers: { 'Authorization': token, 'Content-Type': 'application/json' }
          });
          if (res.status === 401) {
            navigate('/signin');
            return;
          }
          if (!res.ok) break;
          const pageData = await res.json();
          if (pageData.status === 'success' && pageData.data) {
            collected = [...collected, ...pageData.data];
          }
        }

        setAllStudents(collected);
      } catch (error) {
        console.error('학생 목록 로딩 실패:', error);
        setAllStudents([]);
      } finally {
        setIsLoadingStudents(false);
      }
    };

    fetchAllStudents();
  }, [isOpen, getAccessToken, navigate, userId, userType]);

  // 전체 학생 로드 완료 후 테이블 높이 측정
  useEffect(() => {
    if (!isLoadingStudents && allStudents.length > 0 && tableContainerRef.current) {
      // 다음 렌더 사이클에서 측정 (테이블 렌더 완료 후)
      requestAnimationFrame(() => {
        if (tableContainerRef.current) {
          setTableHeight(tableContainerRef.current.offsetHeight);
        }
      });
    }
  }, [isLoadingStudents, allStudents.length]);

  // 클라이언트 필터링
  const students = useMemo(() => {
    let filtered = allStudents;
    const nameQuery = searchQuery.trim().toLowerCase();
    const lecQuery = lectureQuery.trim().toLowerCase();
    const tQuery = teacherQuery.trim().toLowerCase();

    if (nameQuery) {
      filtered = filtered.filter(s =>
        (s.student_name || '').toLowerCase().includes(nameQuery)
      );
    }
    if (lecQuery) {
      filtered = filtered.filter(s =>
        (s.lecture || '').toLowerCase().includes(lecQuery)
      );
    }
    if (tQuery && userType === 'manager') {
      filtered = filtered.filter(s =>
        (s.in_charge_teacher || '').toLowerCase().includes(tQuery)
      );
    }
    return filtered;
  }, [allStudents, searchQuery, lectureQuery, teacherQuery, userType]);

  // 검색 활성 여부
  const isSearchActive = searchQuery.trim() || lectureQuery.trim() || teacherQuery.trim();

  // 전체 선택 상태
  const { isAllSelected, isIndeterminate } = useMemo(() => {
    if (!isSearchActive || students.length === 0) {
      return { isAllSelected: false, isIndeterminate: false };
    }
    const selectedIds = new Set(selectedStudents.map(s => s.student_id));
    const selectedCount = students.filter(s => selectedIds.has(s.student_id)).length;
    return {
      isAllSelected: selectedCount === students.length,
      isIndeterminate: selectedCount > 0 && selectedCount < students.length,
    };
  }, [isSearchActive, students, selectedStudents]);

  // 전체 선택/해제 토글
  const toggleSelectAll = useCallback(() => {
    if (isAllSelected) {
      // 필터된 학생만 해제
      const filteredIds = new Set(students.map(s => s.student_id));
      setSelectedStudents(prev => prev.filter(s => !filteredIds.has(s.student_id)));
    } else {
      // 필터된 학생 전체 추가 (기존 선택 유지)
      setSelectedStudents(prev => {
        const existingIds = new Set(prev.map(s => s.student_id));
        const newStudents = students.filter(s => !existingIds.has(s.student_id));
        return [...prev, ...newStudents];
      });
    }
  }, [isAllSelected, students]);

  // indeterminate 콜백 ref
  const selectAllRef = useCallback((el) => {
    if (el) {
      el.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  // 체크박스 토글
  const toggleStudent = (student) => {
    setSelectedStudents(prev => {
      const exists = prev.find(s => s.student_id === student.student_id);
      if (exists) {
        return prev.filter(s => s.student_id !== student.student_id);
      }
      return [...prev, student];
    });
  };

  // Step 2에서 개별 학생 제거
  const removeStudent = (studentId) => {
    setSelectedStudents(prev => prev.filter(s => s.student_id !== studentId));
  };

  // 배정 처리
  const handleAssign = async () => {
    setIsAssigning(true);
    setStep(3);
    const assignResults = [];

    try {
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return;
      }

      // 1. 프리셋 상세 조회 → tree_id 획득
      const presetResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/presets/${presetId}`,
        { headers: { 'Authorization': token } }
      );

      if (presetResponse.status === 401) {
        navigate('/signin');
        return;
      }

      if (!presetResponse.ok) {
        // 전체 중단
        for (const student of selectedStudents) {
          assignResults.push({
            student,
            status: 'fail',
            reason: '프리셋 정보를 불러올 수 없습니다'
          });
        }
        setResults(assignResults);
        setIsAssigning(false);
        return;
      }

      const presetData = await presetResponse.json().catch(() => ({}));
      const treeId = presetData.data?.tree_id;

      if (!treeId) {
        for (const student of selectedStudents) {
          assignResults.push({
            student,
            status: 'fail',
            reason: '교육과정 정보를 찾을 수 없습니다'
          });
        }
        setResults(assignResults);
        setIsAssigning(false);
        return;
      }

      // 2. 각 학생별 순차 처리
      for (const student of selectedStudents) {
        try {
          // 2-1. 학생 스탬프 확인
          const stampsResponse = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/exams?student_id=${student.student_id}`,
            { headers: { 'Authorization': token } }
          );

          if (stampsResponse.status === 401) {
            navigate('/signin');
            return;
          }

          let stampId = null;

          if (stampsResponse.ok) {
            const stampsData = await stampsResponse.json().catch(() => ({}));
            const stamps = Array.isArray(stampsData.data?.stamps) ? stampsData.data.stamps : [];
            const matchingStamp = stamps.find(s => s.tree_id === treeId);
            if (matchingStamp) {
              stampId = matchingStamp.stamp_id;
            }
          }

          // 2-2. 스탬프 없으면 생성
          if (!stampId) {
            const createStampResponse = await fetch(
              `${process.env.REACT_APP_API_BASE_URL}/api/exams`,
              {
                method: 'POST',
                headers: {
                  'Authorization': token,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  tree_id: treeId,
                  student_id: student.student_id
                })
              }
            );

            if (createStampResponse.status === 409) {
              // 이미 존재 → 재조회
              const retryResponse = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/api/exams?student_id=${student.student_id}`,
                { headers: { 'Authorization': token } }
              );
              if (retryResponse.ok) {
                const retryData = await retryResponse.json().catch(() => ({}));
                const retryStamps = Array.isArray(retryData.data?.stamps) ? retryData.data.stamps : [];
                const match = retryStamps.find(s => s.tree_id === treeId);
                if (match) {
                  stampId = match.stamp_id;
                }
              }
              if (!stampId) {
                assignResults.push({
                  student,
                  status: 'fail',
                  reason: '교육과정 등록 실패'
                });
                setResults([...assignResults]);
                continue;
              }
            } else if (createStampResponse.status === 401) {
              navigate('/signin');
              return;
            } else if (!createStampResponse.ok) {
              assignResults.push({
                student,
                status: 'fail',
                reason: '교육과정 등록 실패'
              });
              setResults([...assignResults]);
              continue;
            } else {
              const createData = await createStampResponse.json().catch(() => ({}));
              stampId = createData.data?.stamp_id || createData.stamp_id;
              if (!stampId) {
                // stamp_id를 응답에서 못 얻으면 재조회
                const retryResponse = await fetch(
                  `${process.env.REACT_APP_API_BASE_URL}/api/exams?student_id=${student.student_id}`,
                  { headers: { 'Authorization': token } }
                );
                if (retryResponse.ok) {
                  const retryData = await retryResponse.json().catch(() => ({}));
                  const retryStamps = Array.isArray(retryData.data?.stamps) ? retryData.data.stamps : [];
                  const match = retryStamps.find(s => s.tree_id === treeId);
                  if (match) stampId = match.stamp_id;
                }
              }
              if (!stampId) {
                assignResults.push({
                  student,
                  status: 'fail',
                  reason: '교육과정 등록 실패'
                });
                setResults([...assignResults]);
                continue;
              }
            }
          }

          // 2-3. 테스트 출제
          const examResponse = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/exams/${stampId}/preset`,
            {
              method: 'POST',
              headers: {
                'Authorization': token,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                preset_id: presetId,
                solved_exception: false
              })
            }
          );

          if (examResponse.status === 401) {
            navigate('/signin');
            return;
          }

          if (examResponse.ok) {
            assignResults.push({
              student,
              status: 'success',
              reason: '출제 완료'
            });
          } else if (examResponse.status === 409) {
            assignResults.push({
              student,
              status: 'fail',
              reason: '미완료 테스트가 있어 출제할 수 없습니다'
            });
          } else {
            const errorData = await examResponse.json().catch(() => ({}));
            assignResults.push({
              student,
              status: 'fail',
              reason: errorData.message || `테스트 생성 실패 (${examResponse.status})`
            });
          }
        } catch (error) {
          assignResults.push({
            student,
            status: 'fail',
            reason: error.message || '알 수 없는 오류'
          });
        }

        // 진행 상황 실시간 업데이트
        setResults([...assignResults]);
      }
    } catch (error) {
      console.error('배정 처리 중 오류:', error);
    } finally {
      setIsAssigning(false);
      if (onAssignSuccess) onAssignSuccess();
    }
  };

  // Step 1: 학생 검색 및 선택
  const renderStep1 = () => (
    <div className="flex flex-col gap-4">
      <div className="rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-700">
        커스텀이름: <span className="font-semibold">{presetName}</span>
      </div>

      {/* 검색 입력 */}
      <div className={`grid gap-2 ${userType === 'manager' ? 'grid-cols-3' : 'grid-cols-2'}`}>
        <input
          type="text"
          placeholder="학생 검색"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full rounded-lg border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
        <input
          type="text"
          placeholder="강의 검색"
          value={lectureQuery}
          onChange={(e) => setLectureQuery(e.target.value)}
          className="w-full rounded-lg border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
        {userType === 'manager' && (
          <input
            type="text"
            placeholder="강사 검색"
            value={teacherQuery}
            onChange={(e) => setTeacherQuery(e.target.value)}
            className="w-full rounded-lg border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        )}
      </div>

      {/* 학생 목록 테이블 */}
      <div
        ref={tableContainerRef}
        className="max-h-64 overflow-y-auto rounded-lg border"
        style={tableHeight ? { minHeight: tableHeight } : undefined}
      >
        {isLoadingStudents ? (
          <div className="flex h-32 items-center justify-center">
            <div className="size-6 animate-spin rounded-full border-2 border-gray-200 border-t-primary"></div>
          </div>
        ) : allStudents.length === 0 ? (
          <div className="flex h-32 items-center justify-center text-sm text-gray-500">
            등록된 학생이 없습니다.
          </div>
        ) : students.length > 0 ? (
          <table className="w-full text-sm whitespace-nowrap">
            <thead className="sticky top-0 z-10 bg-gray-100">
              <tr>
                <th className="w-10 px-3 py-2">
                  {isSearchActive && (
                    <input
                      type="checkbox"
                      ref={selectAllRef}
                      checked={isAllSelected}
                      onChange={toggleSelectAll}
                      className="size-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                  )}
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-700">SID</th>
                <th className="px-3 py-2 text-left font-medium text-gray-700">학생명</th>
                <th className="px-3 py-2 text-left font-medium text-gray-700">학교</th>
                <th className="px-3 py-2 text-left font-medium text-gray-700">강의</th>
                <th className="px-3 py-2 text-left font-medium text-gray-700">담당강사</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {students.map((student) => {
                const isSelected = selectedStudents.some(s => s.student_id === student.student_id);
                return (
                  <tr
                    key={student.student_id}
                    onClick={() => toggleStudent(student)}
                    className={`cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}
                  >
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        className="size-4 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                    </td>
                    <td className="px-3 py-2 text-gray-600">{student.student_id || '-'}</td>
                    <td className="px-3 py-2 font-medium text-gray-900">{student.student_name || '-'}</td>
                    <td className="px-3 py-2 text-gray-600">
                      {student.school_name
                        ? student.school_grade
                          ? `${student.school_name} (${student.school_grade}학년)`
                          : student.school_name
                        : '-'}
                    </td>
                    <td className="px-3 py-2 text-gray-600">{student.lecture || '-'}</td>
                    <td className="px-3 py-2 text-gray-600">{student.in_charge_teacher || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="flex h-32 items-center justify-center text-sm text-gray-500">
            검색 결과가 없습니다.
          </div>
        )}
      </div>
    </div>
  );

  // Step 2: 확인
  const renderStep2 = () => (
    <div className="flex flex-col gap-4">
      <div className="rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-700">
        커스텀이름: <span className="font-semibold">{presetName}</span>
      </div>

      <div className="text-sm font-medium text-gray-700">
        선택된 학생 ({selectedStudents.length}명)
      </div>

      <div className="max-h-72 overflow-y-auto rounded-lg border">
        <div className="divide-y divide-gray-200">
          {selectedStudents.map((student) => (
            <div key={student.student_id} className="flex items-center justify-between px-4 py-3">
              <div>
                <span className="font-medium text-gray-900">{student.student_name}</span>
                <span className="ml-2 text-sm text-gray-500">
                  {student.school_name || '-'} {student.school_grade ? `(${student.school_grade})` : ''}
                </span>
              </div>
              <button
                onClick={() => removeStudent(student.student_id)}
                className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-red-500"
              >
                <X className="size-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Step 3: 결과
  const renderStep3 = () => {
    const successCount = results.filter(r => r.status === 'success').length;
    const failCount = results.filter(r => r.status === 'fail').length;

    return (
      <div className="flex flex-col gap-4">
        {/* 진행 상태 */}
        {isAssigning && (
          <div className="flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-700">
            <Loader2 className="size-4 animate-spin" />
            처리 중... ({results.length}/{selectedStudents.length})
          </div>
        )}

        {/* 결과 요약 */}
        {!isAssigning && (
          <div className="flex gap-4 rounded-lg bg-gray-50 px-4 py-3 text-sm">
            <span className="text-gray-600">총 {selectedStudents.length}명</span>
            <span className="font-medium text-green-600">성공 {successCount}명</span>
            <span className="font-medium text-red-600">실패 {failCount}명</span>
          </div>
        )}

        {/* 결과 테이블 */}
        <div className="max-h-72 overflow-y-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-700">학생명</th>
                <th className="px-3 py-2 text-center font-medium text-gray-700">상태</th>
                <th className="px-3 py-2 text-left font-medium text-gray-700">사유</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {results.map((result, idx) => (
                <tr key={idx} className={result.status === 'success' ? 'bg-green-50/50' : 'bg-red-50/50'}>
                  <td className="px-3 py-2 font-medium text-gray-900">
                    {result.student.student_name}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {result.status === 'success' ? (
                      <span className="inline-flex items-center gap-1 text-green-600">
                        <CheckCircle className="size-4" />
                        성공
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-red-600">
                        <XCircle className="size-4" />
                        실패
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-600">{result.reason}</td>
                </tr>
              ))}
              {/* 아직 처리되지 않은 학생 표시 */}
              {isAssigning && selectedStudents.slice(results.length).map((student, idx) => (
                <tr key={`pending-${idx}`} className="bg-gray-50/50">
                  <td className="px-3 py-2 text-gray-400">{student.student_name}</td>
                  <td className="px-3 py-2 text-center text-gray-400">대기중</td>
                  <td className="px-3 py-2 text-gray-400">-</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Footer 렌더링
  const renderFooter = () => {
    if (step === 1) {
      return (
        <div className="flex w-full justify-between">
          <button
            onClick={handleClose}
            className="rounded-xl bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300"
          >
            취소
          </button>
          <button
            onClick={() => setStep(2)}
            disabled={selectedStudents.length === 0}
            className="rounded-xl bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover disabled:bg-gray-300"
          >
            선택 완료 ({selectedStudents.length}명)
          </button>
        </div>
      );
    }

    if (step === 2) {
      return (
        <div className="flex w-full justify-between">
          <button
            onClick={() => setStep(1)}
            className="inline-flex items-center gap-1 rounded-xl bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300"
          >
            <ArrowLeft className="size-4" />
            뒤로
          </button>
          <button
            onClick={handleAssign}
            disabled={selectedStudents.length === 0 || isAssigning}
            className="rounded-xl bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover disabled:bg-gray-300"
          >
            {isAssigning ? '배정 중...' : '배정하기'}
          </button>
        </div>
      );
    }

    // Step 3
    return (
      <button
        onClick={handleClose}
        disabled={isAssigning}
        className="rounded-xl bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300 disabled:opacity-50"
      >
        닫기
      </button>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={step === 1 ? '학생 배정' : step === 2 ? '테스트 출제 확인' : '테스트 출제 결과'}
      size="lg"
      zIndex={9999985}
      footer={renderFooter()}
    >
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
    </Modal>
  );
};

export default StudentAssignModal;
